import padixLogo from "@/assets/images/logo.png";
import QRCode from "qrcode";

// Portrait, A-series ratio: ~150dpi on A4, ~210dpi on A5.
export const BANNER_WIDTH = 1240;
export const BANNER_HEIGHT = 1754;

const W = BANNER_WIDTH;
const H = BANNER_HEIGHT;
const PAD = 110;
const CONTENT_W = W - PAD * 2;
const LOGO_CY = 165;
const LOGO_R = 92;
const URL_Y = 1424;
const FOOTER_Y = 1580;
const FOOTER_H = 84;

const BRAND = "پدیکس";
const BRAND_TAGLINE = "سامانه نوبت‌دهی آنلاین";

const FONT = "YekanBakhNoEn, Tahoma, sans-serif";
// YekanBakhNoEn has no Latin glyphs, so the address is drawn in a Latin font.
const LATIN_FONT = "'Segoe UI', Roboto, Tahoma, Arial, sans-serif";

export type BannerContent = { url: string; title: string; description: string; logo?: HTMLImageElement | null };

// With a salon logo on top, the title and description move down and the QR card shrinks a little.
const LAYOUTS = {
  plain: { titleY: 235, titleSize: 92, descCenterY: 375, descSize: 44, descLine: 70, cardY: 560, card: 800, qr: 660 },
  withLogo: { titleY: 335, titleSize: 84, descCenterY: 440, descSize: 42, descLine: 62, cardY: 590, card: 760, qr: 628 },
};

type Theme = {
  paintBackground: (ctx: CanvasRenderingContext2D) => void;
  title: string;
  description: string;
  qr: string;
  cardBorder?: string;
  cardShadow: string;
  badge: string;
  badgeText: string;
  urlPill: string;
  urlBorder?: string;
  urlText: string;
  footer: string;
  footerBorder?: string;
  footerText: string;
};

export type BannerTemplate = { id: string; name: string; theme: Theme };

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function circle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, fill: string) {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fillStyle = fill;
  ctx.fill();
}

// Four-point star, like the one in the Padix logo.
function sparkle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, fill: string) {
  ctx.beginPath();
  ctx.moveTo(x, y - r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.quadraticCurveTo(x, y, x, y + r);
  ctx.quadraticCurveTo(x, y, x - r, y);
  ctx.quadraticCurveTo(x, y, x, y - r);
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
}

function linearFill(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number, stops: string[]) {
  const gradient = ctx.createLinearGradient(x1, y1, x2, y2);
  stops.forEach((color, i) => gradient.addColorStop(i / (stops.length - 1), color));
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, W, H);
}

function waves(ctx: CanvasRenderingContext2D, alphas: number[]) {
  alphas.forEach((alpha, i) => {
    const top = H - 330 + i * 110;
    ctx.beginPath();
    ctx.moveTo(0, top);
    ctx.bezierCurveTo(W * 0.3, top - 140, W * 0.6, top + 140, W, top - 40);
    ctx.lineTo(W, H);
    ctx.lineTo(0, H);
    ctx.closePath();
    ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
    ctx.fill();
  });
}

function hazardBand(ctx: CanvasRenderingContext2D, y: number, height: number, color: string) {
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, y, W, height);
  ctx.clip();
  ctx.fillStyle = color;
  for (let x = -height; x < W + height; x += 90) {
    ctx.beginPath();
    ctx.moveTo(x, y + height);
    ctx.lineTo(x + height, y);
    ctx.lineTo(x + height + 45, y);
    ctx.lineTo(x + 45, y + height);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
}

// Shrinks the font until the text fits; returns the size used.
function fitFont(ctx: CanvasRenderingContext2D, text: string, weight: number, size: number, min: number, maxWidth: number, family = FONT) {
  let current = size;
  ctx.font = `${weight} ${current}px ${family}`;
  while (current > min && ctx.measureText(text).width > maxWidth) {
    current -= 2;
    ctx.font = `${weight} ${current}px ${family}`;
  }
  return current;
}

function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, maxLines: number) {
  const lines: string[] = [];
  for (const paragraph of text.split("\n")) {
    let line = "";
    for (const word of paragraph.trim().split(/\s+/).filter(Boolean)) {
      const next = line ? `${line} ${word}` : word;
      if (!line || ctx.measureText(next).width <= maxWidth) line = next;
      else {
        lines.push(line);
        line = word;
      }
    }
    if (line) lines.push(line);
  }
  if (lines.length <= maxLines) return lines;
  const kept = lines.slice(0, maxLines);
  let last = kept[maxLines - 1];
  while (last && ctx.measureText(`${last}…`).width > maxWidth) last = last.slice(0, -1);
  kept[maxLines - 1] = `${last.trimEnd()}…`;
  return kept;
}

export const BANNER_TEMPLATES: BannerTemplate[] = [
  {
    id: "classic",
    name: "ساده و روشن",
    theme: {
      paintBackground: (ctx) => {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, W, H);
        circle(ctx, W + 60, -60, 380, "#eff2ff");
        circle(ctx, -80, H + 40, 320, "#eff2ff");
        circle(ctx, 150, 180, 26, "#c7cffc");
        circle(ctx, W - 170, H - 250, 18, "#c7cffc");
        roundRect(ctx, 44, 44, W - 88, H - 88, 56);
        ctx.strokeStyle = "#4f5bd5";
        ctx.lineWidth = 6;
        ctx.stroke();
      },
      title: "#1b1f3b",
      description: "#5b6275",
      qr: "#1b1f3b",
      cardBorder: "#e3e7ff",
      cardShadow: "rgba(27, 31, 59, 0.10)",
      badge: "#4f5bd5",
      badgeText: "#ffffff",
      urlPill: "#eff2ff",
      urlText: "#36437a",
      footer: "#ffffff",
      footerBorder: "#e3e7ff",
      footerText: "#36437a",
    },
  },
  {
    id: "padix",
    name: "بنفش پدیکس",
    theme: {
      paintBackground: (ctx) => {
        linearFill(ctx, 0, 0, W, H, ["#4a1942", "#37142f", "#1f0a1b"]);
        circle(ctx, W + 40, 200, 340, "rgba(135, 82, 200, 0.28)");
        circle(ctx, -60, H - 360, 300, "rgba(135, 82, 200, 0.22)");
        sparkle(ctx, 170, 150, 46, "#ff7352");
        sparkle(ctx, W - 110, 720, 30, "rgba(255, 115, 82, 0.8)");
        sparkle(ctx, 130, 1100, 24, "rgba(255, 255, 255, 0.35)");
        sparkle(ctx, W - 120, H - 230, 40, "#ff7352");
      },
      title: "#ffffff",
      description: "rgba(255, 255, 255, 0.8)",
      qr: "#37142f",
      cardShadow: "rgba(0, 0, 0, 0.4)",
      badge: "#ff7352",
      badgeText: "#ffffff",
      urlPill: "rgba(135, 82, 200, 0.35)",
      urlBorder: "#8752c8",
      urlText: "#ffffff",
      footer: "#ffffff",
      footerText: "#37142f",
    },
  },
  {
    id: "violet",
    name: "بنفش گرادیانی",
    theme: {
      paintBackground: (ctx) => {
        linearFill(ctx, 0, 0, W, H, ["#4f46e5", "#7c3aed", "#a21caf"]);
        circle(ctx, W - 40, 120, 300, "rgba(255, 255, 255, 0.08)");
        circle(ctx, 60, H - 260, 360, "rgba(255, 255, 255, 0.07)");
        circle(ctx, 180, 500, 70, "rgba(255, 255, 255, 0.10)");
        circle(ctx, W - 160, H - 300, 50, "rgba(255, 255, 255, 0.12)");
      },
      title: "#ffffff",
      description: "rgba(255, 255, 255, 0.85)",
      qr: "#1e1b4b",
      cardShadow: "rgba(15, 10, 60, 0.35)",
      badge: "#facc15",
      badgeText: "#1e1b4b",
      urlPill: "rgba(255, 255, 255, 0.16)",
      urlBorder: "rgba(255, 255, 255, 0.35)",
      urlText: "#ffffff",
      footer: "rgba(255, 255, 255, 0.14)",
      footerText: "#ffffff",
    },
  },
  {
    id: "sunset",
    name: "غروب گرم",
    theme: {
      paintBackground: (ctx) => {
        linearFill(ctx, 0, 0, W, H, ["#f97316", "#f43f5e", "#db2777"]);
        waves(ctx, [0.12, 0.18]);
        circle(ctx, W - 120, 140, 180, "rgba(255, 255, 255, 0.12)");
      },
      title: "#ffffff",
      description: "rgba(255, 255, 255, 0.9)",
      qr: "#7c2d12",
      cardShadow: "rgba(124, 45, 18, 0.35)",
      badge: "#7c2d12",
      badgeText: "#ffffff",
      urlPill: "#ffffff",
      urlText: "#c2410c",
      footer: "rgba(255, 255, 255, 0.2)",
      footerText: "#ffffff",
    },
  },
  {
    id: "ocean",
    name: "آبی اقیانوسی",
    theme: {
      paintBackground: (ctx) => {
        linearFill(ctx, 0, 0, W, H, ["#06b6d4", "#2563eb", "#1e3a8a"]);
        waves(ctx, [0.08, 0.14]);
        circle(ctx, 120, 160, 200, "rgba(255, 255, 255, 0.10)");
        circle(ctx, W - 140, 620, 60, "rgba(255, 255, 255, 0.12)");
      },
      title: "#ffffff",
      description: "rgba(255, 255, 255, 0.88)",
      qr: "#1e3a8a",
      cardShadow: "rgba(15, 23, 42, 0.35)",
      badge: "#22d3ee",
      badgeText: "#0c4a6e",
      urlPill: "rgba(255, 255, 255, 0.16)",
      urlBorder: "rgba(255, 255, 255, 0.35)",
      urlText: "#ffffff",
      footer: "rgba(255, 255, 255, 0.16)",
      footerText: "#ffffff",
    },
  },
  {
    id: "luxury",
    name: "مشکی و طلایی",
    theme: {
      paintBackground: (ctx) => {
        const gradient = ctx.createRadialGradient(W / 2, H * 0.45, 100, W / 2, H * 0.45, H * 0.75);
        gradient.addColorStop(0, "#1f2937");
        gradient.addColorStop(1, "#0b0f19");
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, W, H);
        ctx.strokeStyle = "#d4af37";
        ctx.lineWidth = 5;
        ctx.strokeRect(50, 50, W - 100, H - 100);
        ctx.lineWidth = 2;
        ctx.strokeRect(72, 72, W - 144, H - 144);
        [[72, 72], [W - 72, 72], [72, H - 72], [W - 72, H - 72]].forEach(([x, y]) => {
          ctx.beginPath();
          ctx.moveTo(x, y - 22);
          ctx.lineTo(x + 22, y);
          ctx.lineTo(x, y + 22);
          ctx.lineTo(x - 22, y);
          ctx.closePath();
          ctx.fillStyle = "#d4af37";
          ctx.fill();
        });
      },
      title: "#e8c872",
      description: "#d1d5db",
      qr: "#111827",
      cardBorder: "#d4af37",
      cardShadow: "rgba(0, 0, 0, 0.5)",
      badge: "#d4af37",
      badgeText: "#111827",
      urlPill: "rgba(212, 175, 55, 0.12)",
      urlBorder: "#d4af37",
      urlText: "#e8c872",
      footer: "rgba(255, 255, 255, 0.05)",
      footerBorder: "rgba(212, 175, 55, 0.6)",
      footerText: "#e8c872",
    },
  },
  {
    id: "mint",
    name: "سبز طبیعی",
    theme: {
      paintBackground: (ctx) => {
        linearFill(ctx, 0, 0, 0, H, ["#f0fdf4", "#d1fae5"]);
        circle(ctx, -40, 160, 260, "rgba(16, 185, 129, 0.14)");
        circle(ctx, W + 30, 420, 220, "rgba(16, 185, 129, 0.10)");
        circle(ctx, W - 200, H - 80, 280, "rgba(16, 185, 129, 0.14)");
        circle(ctx, 200, H - 280, 60, "rgba(16, 185, 129, 0.18)");
      },
      title: "#064e3b",
      description: "#047857",
      qr: "#064e3b",
      cardBorder: "#a7f3d0",
      cardShadow: "rgba(6, 78, 59, 0.15)",
      badge: "#10b981",
      badgeText: "#ffffff",
      urlPill: "#064e3b",
      urlText: "#ffffff",
      footer: "#ffffff",
      footerBorder: "#a7f3d0",
      footerText: "#064e3b",
    },
  },
  {
    id: "rose",
    name: "صورتی ملایم",
    theme: {
      paintBackground: (ctx) => {
        linearFill(ctx, 0, 0, W, H, ["#fff1f2", "#fce7f3"]);
        circle(ctx, W + 20, -20, 340, "rgba(244, 63, 94, 0.10)");
        circle(ctx, -60, H - 420, 260, "rgba(236, 72, 153, 0.08)");
        sparkle(ctx, 160, 150, 34, "#fb7185");
        sparkle(ctx, W - 140, 520, 24, "#f9a8d4");
        sparkle(ctx, 140, 1260, 28, "#f9a8d4");
        sparkle(ctx, W - 170, H - 260, 34, "#fb7185");
      },
      title: "#881337",
      description: "#9f1239",
      qr: "#881337",
      cardBorder: "#fecdd3",
      cardShadow: "rgba(136, 19, 55, 0.12)",
      badge: "#e11d48",
      badgeText: "#ffffff",
      urlPill: "#881337",
      urlText: "#ffffff",
      footer: "#ffffff",
      footerBorder: "#fecdd3",
      footerText: "#881337",
    },
  },
  {
    id: "lavender",
    name: "یاسی",
    theme: {
      paintBackground: (ctx) => {
        linearFill(ctx, 0, 0, 0, H, ["#f5f3ff", "#ede9fe"]);
        circle(ctx, -80, 260, 300, "rgba(139, 92, 246, 0.10)");
        circle(ctx, W + 60, H - 500, 320, "rgba(139, 92, 246, 0.10)");
        sparkle(ctx, W - 170, 160, 44, "#a78bfa");
        sparkle(ctx, W - 250, 250, 20, "#c4b5fd");
        sparkle(ctx, 150, H - 280, 36, "#a78bfa");
      },
      title: "#4c1d95",
      description: "#6d28d9",
      qr: "#4c1d95",
      cardBorder: "#ddd6fe",
      cardShadow: "rgba(76, 29, 149, 0.12)",
      badge: "#7c3aed",
      badgeText: "#ffffff",
      urlPill: "#4c1d95",
      urlText: "#ffffff",
      footer: "#ffffff",
      footerBorder: "#ddd6fe",
      footerText: "#4c1d95",
    },
  },
  {
    id: "cream",
    name: "کرم و قهوه‌ای",
    theme: {
      paintBackground: (ctx) => {
        ctx.fillStyle = "#fbf5ec";
        ctx.fillRect(0, 0, W, H);
        // Arch behind the card.
        const x0 = 150;
        const r = (W - x0 * 2) / 2;
        const top = 300;
        ctx.beginPath();
        ctx.moveTo(x0, 1545);
        ctx.lineTo(x0, top + r);
        ctx.arc(W / 2, top + r, r, Math.PI, 0);
        ctx.lineTo(W - x0, 1545);
        ctx.closePath();
        ctx.fillStyle = "#f3e5d0";
        ctx.fill();
        ctx.strokeStyle = "#b7791f";
        ctx.lineWidth = 3;
        ctx.strokeRect(56, 56, W - 112, H - 112);
      },
      title: "#5b3a1e",
      description: "#8a6a4a",
      qr: "#5b3a1e",
      cardBorder: "#e7d3b5",
      cardShadow: "rgba(91, 58, 30, 0.12)",
      badge: "#b7791f",
      badgeText: "#ffffff",
      urlPill: "#5b3a1e",
      urlText: "#fbf5ec",
      footer: "#ffffff",
      footerBorder: "#e7d3b5",
      footerText: "#5b3a1e",
    },
  },
  {
    id: "yellow",
    name: "زرد پررنگ",
    theme: {
      paintBackground: (ctx) => {
        ctx.fillStyle = "#facc15";
        ctx.fillRect(0, 0, W, H);
        hazardBand(ctx, 0, 60, "#111827");
        hazardBand(ctx, H - 60, 60, "#111827");
        circle(ctx, W - 130, 470, 44, "rgba(17, 24, 39, 0.10)");
        circle(ctx, 130, 1250, 64, "rgba(17, 24, 39, 0.08)");
      },
      title: "#111827",
      description: "#1f2937",
      qr: "#111827",
      cardBorder: "#111827",
      cardShadow: "rgba(17, 24, 39, 0.18)",
      badge: "#111827",
      badgeText: "#facc15",
      urlPill: "#111827",
      urlText: "#facc15",
      footer: "#ffffff",
      footerBorder: "#111827",
      footerText: "#111827",
    },
  },
];

let fontsReady: Promise<unknown> | undefined;
const loadFonts = () =>
  (fontsReady ??= Promise.all(
    [`800 40px ${FONT}`, `700 40px ${FONT}`, `400 40px ${FONT}`].map((font) =>
      Promise.resolve(document.fonts?.load?.(font)).catch(() => undefined),
    ),
  ));

let logoReady: Promise<HTMLImageElement | undefined> | undefined;
const loadLogo = () =>
  (logoReady ??= new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(undefined);
    image.src = padixLogo;
  }));

// "[logo] پدیکس | سامانه نوبت‌دهی آنلاین" chip at the bottom of every banner.
function drawFooter(ctx: CanvasRenderingContext2D, t: Theme, logo?: HTMLImageElement) {
  const logoD = 60;
  const gap = 16;
  const cy = FOOTER_Y + FOOTER_H / 2;
  ctx.font = `800 34px ${FONT}`;
  const brandW = ctx.measureText(BRAND).width;
  ctx.font = `400 30px ${FONT}`;
  const taglineW = ctx.measureText(BRAND_TAGLINE).width;
  const total = 12 + logoD + gap + brandW + gap + 2 + gap + taglineW + 34;
  const x = (W - total) / 2;

  roundRect(ctx, x, FOOTER_Y, total, FOOTER_H, FOOTER_H / 2);
  ctx.fillStyle = t.footer;
  ctx.fill();
  if (t.footerBorder) {
    ctx.strokeStyle = t.footerBorder;
    ctx.lineWidth = 3;
    ctx.stroke();
  }

  let right = x + total - 12;
  circle(ctx, right - logoD / 2, cy, logoD / 2, "#ffffff");
  if (logo) {
    ctx.save();
    ctx.beginPath();
    ctx.arc(right - logoD / 2, cy, logoD / 2, 0, Math.PI * 2);
    ctx.clip();
    // The logo file has wide white margins around the mark.
    ctx.drawImage(logo, right - logoD - 8, cy - logoD / 2 - 8, logoD + 16, logoD + 16);
    ctx.restore();
  }
  right -= logoD + gap;

  ctx.direction = "rtl";
  ctx.textAlign = "right";
  ctx.textBaseline = "middle";
  ctx.fillStyle = t.footerText;
  ctx.font = `800 34px ${FONT}`;
  ctx.fillText(BRAND, right, cy + 2);
  right -= brandW + gap;
  ctx.globalAlpha = 0.4;
  ctx.fillRect(right - 2, cy - 18, 2, 36);
  ctx.globalAlpha = 1;
  right -= 2 + gap;
  ctx.font = `400 30px ${FONT}`;
  ctx.fillText(BRAND_TAGLINE, right, cy + 2);
}

// Salon logo in a white ring at the top, scaled to fit inside the circle.
function drawMerchantLogo(ctx: CanvasRenderingContext2D, t: Theme, logo: HTMLImageElement, scale: number) {
  ctx.save();
  ctx.shadowColor = t.cardShadow;
  ctx.shadowBlur = 40 * scale;
  ctx.shadowOffsetY = 12 * scale;
  circle(ctx, W / 2, LOGO_CY, LOGO_R, "#ffffff");
  ctx.restore();

  ctx.save();
  ctx.beginPath();
  ctx.arc(W / 2, LOGO_CY, LOGO_R - 8, 0, Math.PI * 2);
  ctx.clip();
  const box = LOGO_R * 1.6;
  const ratio = Math.min(box / logo.naturalWidth, box / logo.naturalHeight);
  const w = logo.naturalWidth * ratio;
  const h = logo.naturalHeight * ratio;
  ctx.drawImage(logo, W / 2 - w / 2, LOGO_CY - h / 2, w, h);
  ctx.restore();

  ctx.beginPath();
  ctx.arc(W / 2, LOGO_CY, LOGO_R, 0, Math.PI * 2);
  ctx.strokeStyle = t.badge;
  ctx.lineWidth = 6;
  ctx.stroke();
}

// Draws the banner at BANNER_WIDTH × BANNER_HEIGHT times `scale` (use a small scale for previews).
export async function renderBanner(template: BannerTemplate, content: BannerContent, scale = 1) {
  const [, padixImage] = await Promise.all([loadFonts(), loadLogo()]);
  const t = template.theme;
  const layout = content.logo ? LAYOUTS.withLogo : LAYOUTS.plain;
  const cardX = (W - layout.card) / 2;
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(W * scale);
  canvas.height = Math.round(H * scale);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas is not supported");
  ctx.scale(scale, scale);
  t.paintBackground(ctx);
  if (content.logo) drawMerchantLogo(ctx, t, content.logo, scale);

  ctx.textAlign = "center";
  ctx.direction = "rtl";
  ctx.fillStyle = t.title;
  fitFont(ctx, content.title, 800, layout.titleSize, 52, CONTENT_W);
  ctx.fillText(content.title, W / 2, layout.titleY, CONTENT_W);

  ctx.fillStyle = t.description;
  ctx.font = `400 ${layout.descSize}px ${FONT}`;
  const lines = wrapLines(ctx, content.description, CONTENT_W, 3);
  const firstLine = layout.descCenterY - ((lines.length - 1) * layout.descLine) / 2;
  lines.forEach((line, i) => ctx.fillText(line, W / 2, firstLine + i * layout.descLine, CONTENT_W));

  // QR card; its white padding doubles as the code's quiet zone.
  ctx.save();
  ctx.shadowColor = t.cardShadow;
  ctx.shadowBlur = 70 * scale;
  ctx.shadowOffsetY = 24 * scale;
  roundRect(ctx, cardX, layout.cardY, layout.card, layout.card, 56);
  ctx.fillStyle = "#ffffff";
  ctx.fill();
  ctx.restore();
  if (t.cardBorder) {
    ctx.strokeStyle = t.cardBorder;
    ctx.lineWidth = 8;
    ctx.stroke();
  }

  const qr = document.createElement("canvas");
  await QRCode.toCanvas(qr, content.url, {
    width: Math.max(64, Math.round(layout.qr * scale)),
    margin: 1,
    errorCorrectionLevel: "Q",
    color: { dark: t.qr, light: "#ffffff" },
  });
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(qr, (W - layout.qr) / 2, layout.cardY + (layout.card - layout.qr) / 2, layout.qr, layout.qr);
  ctx.imageSmoothingEnabled = true;

  // "Scan me" badge sitting on the card's top edge.
  const badgeLabel = "اسکن کنید";
  ctx.font = `700 40px ${FONT}`;
  const badgeW = ctx.measureText(badgeLabel).width + 110;
  roundRect(ctx, (W - badgeW) / 2, layout.cardY - 42, badgeW, 84, 42);
  ctx.fillStyle = t.badge;
  ctx.fill();
  ctx.fillStyle = t.badgeText;
  ctx.textBaseline = "middle";
  ctx.fillText(badgeLabel, W / 2, layout.cardY + 3);

  const address = content.url.replace(/^https?:\/\//, "");
  ctx.direction = "ltr";
  fitFont(ctx, address, 700, 46, 26, CONTENT_W - 120, LATIN_FONT);
  const pillW = Math.min(CONTENT_W, ctx.measureText(address).width + 120);
  roundRect(ctx, (W - pillW) / 2, URL_Y, pillW, 96, 48);
  ctx.fillStyle = t.urlPill;
  ctx.fill();
  if (t.urlBorder) {
    ctx.strokeStyle = t.urlBorder;
    ctx.lineWidth = 3;
    ctx.stroke();
  }
  ctx.fillStyle = t.urlText;
  ctx.fillText(address, W / 2, URL_Y + 50, CONTENT_W - 60);

  drawFooter(ctx, t, padixImage);
  return canvas;
}
